import { useCallback, useEffect, useState } from "react";
import { Button, ToggleButtonGroup, ToggleButton, Stack, IconButton, Typography } from "@mui/material";
import { Box } from "@mui/system";
import ReactJson from "react-json-view";

import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import SettingsIcon from "@mui/icons-material/Settings";
import RefreshIcon from "@mui/icons-material/Refresh";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import { useApi } from "../../utils/ApiProvider";
import { useDevice } from "../../utils/DeviceProvider";
import { FormattedMessage, useIntl } from "react-intl";
import { BeforeSaveErrorType, BeforeSaveFunction, reservedTemplateKeys } from "./BasicTemplate";
import { isEqual } from "lodash-es";
import { useErrorHandler } from "../../hooks/useErrorHandler";
import { useMutation } from "react-query";
import { UnitConfigResponse } from "../../utils/types";

interface ConfigSampleProps<T> {
  beforeSave?: BeforeSaveFunction<T>;
  loadConfig?: (config: any) => void;
}

const ConfigSample = ({ beforeSave, loadConfig }: ConfigSampleProps<any>) => {
  const [deviceConfig, setDeviceConfig] = useState<any>();
  const [type, setType] = useState(sessionStorage.getItem("config_sample_type") || "device");
  const [source, setSource] = useState(sessionStorage.getItem("config_sample_source") || "current");
  const [sample, setSample] = useState<object | string | null | undefined>(null);
  const [settingsVisible, setSettingsVisible] = useState(false);
  const [errors, setErrors] = useState<BeforeSaveErrorType[]>([]);
  const [display, setDisplay] = useState<string[]>([]);
  const [errMsg, setErrMsg] = useState("");
  const [active] = useState({ active: true });

  const {
    device: { unitid, currentValue, config, template },
  } = useDevice();

  const api = useApi();
  const { formatMessage } = useIntl();

  const { errorHandler: onError } = useErrorHandler([
    [{ status: 400, matcher: ({ error }) => error.data.dualUnitid !== undefined }, () => null],
    [
      { status: 400, matcher: ({ error }) => error.data.dualUnitid === undefined },
      () => formatMessage({ defaultMessage: "Incorrect template format for unit {unitid}." }, { unitid }),
    ],
  ]);
  const defaultConfigQuery = useCallback(() => api(`/units/${unitid}/config/default`), [api, unitid]);
  const onDefaultConfiguccess = useCallback(
    (response) => {
      if (!loadConfig) {
        return;
      }
      loadConfig(response);
    },
    [loadConfig]
  );
  const { mutate: loadDefaultConfig, isLoading: defaultConfigLoading } = useMutation({
    mutationFn: defaultConfigQuery,
    onSuccess: onDefaultConfiguccess,
    onError,
  });

  const { mutate: getSavedConfig, data: savedToolboxConfig } = useMutation({
    mutationFn: () => api<UnitConfigResponse>(`/units/${unitid}/config`).then((res) => res?.toolbox_config ?? null),
    onSuccess: onDefaultConfiguccess,
    onError: (err: any) => {
      if (err.status === 404) {
        return;
      }
      onError(err);
    },
  });

  const getDeviceConfig = useCallback(
    (value: any) => {
      setErrMsg("");
      if (!value) {
        return;
      }
      api<any>(`/config-debug/${unitid}`, { method: "POST", body: JSON.stringify(value) })
        .then((deviceConfig) => {
          if (!active.active) {
            return;
          }
          setDeviceConfig(deviceConfig.old ? deviceConfig.old : deviceConfig);
        })
        .catch((err) => {
          if (err.status === 404) {
            setErrMsg(formatMessage({ defaultMessage: "You don't have permission to configure this device." }));
          } else {
            setErrMsg(formatMessage({ defaultMessage: "Device config query error{error, select, null {} other{: {error}}}" }, { error: err.message }));
          }
        });
    },
    [unitid, api, active, formatMessage]
  );

  const handleTypeChange = useCallback((event, value) => {
    if (!value) {
      return;
    }
    sessionStorage.setItem("config_sample_type", value);
    setType(value);
  }, []);

  const handleSourceChange = useCallback((event, value) => {
    if (!value) {
      return;
    }
    sessionStorage.setItem("config_sample_source", value);
    setSource(value);
  }, []);

  const processCurrentData = useCallback(
    (currentValue: any) => {
      if (!beforeSave || !currentValue) {
        return currentValue;
      }
      const { processed, errors } = beforeSave(currentValue);
      setErrors(errors || []);

      return processed;
    },
    [beforeSave]
  );

  const keySort = useCallback(
    (value) => {
      if (template) {
        if (!value) {
          return value;
        }
        const correctKeyOrder = [
          "accountinfo",
          ...Object.keys(template.template).filter((key) => !reservedTemplateKeys.includes(key) && value[key]),
          "templateId",
          "configVersion",
        ];
        if (isEqual(Object.keys(value), correctKeyOrder)) {
          return value;
        }
        const res = Object.fromEntries(correctKeyOrder.map((key: string) => [key, value[key]]));
        return res;
      }
    },
    [template]
  );

  const copyToClipboard = useCallback((sample) => {
    navigator.clipboard.writeText(sample);
  }, []);

  useEffect(() => {
    active.active = true;

    return () => {
      active.active = false;
    };
  }, [active]);

  useEffect(() => getSavedConfig(), [getSavedConfig]);

  useEffect(() => {
    getDeviceConfig(processCurrentData(currentValue));
  }, [currentValue, getDeviceConfig, beforeSave, processCurrentData]);

  useEffect(() => {
    if (type === "template") {
      setSample(template?.template);
    } else if (type === "device") {
      if (source === "current") {
        setSample(deviceConfig);
      } else {
        try {
          if (!config?.config) {
            throw new Error();
          }
          const parsed = JSON.parse(config.config);
          setSample(parsed);
        } catch (err) {
          setSample(config?.config);
        }
      }
    } else {
      if (source === "current") {
        setSample(processCurrentData(currentValue));
      } else {
        setSample(config?.id ? config.toolboxConfig : null);
      }
    }
  }, [type, source, currentValue, config, template, deviceConfig, processCurrentData, savedToolboxConfig]);

  return (
    <>
      <Stack direction="row" spacing={4} alignSelf="center" flexWrap="wrap" justifyContent="center">
        <ToggleButtonGroup color="primary" value={type} exclusive onChange={handleTypeChange} sx={{ paddingBottom: 2 }}>
          <ToggleButton value="template">
            <FormattedMessage defaultMessage="Template" />
          </ToggleButton>
          <ToggleButton value="device">
            <FormattedMessage defaultMessage="Device" />
          </ToggleButton>
          <ToggleButton value="toolbox">Toolbox</ToggleButton>
        </ToggleButtonGroup>
        <ToggleButtonGroup
          color="primary"
          value={type === "template" ? null : source}
          exclusive
          onChange={handleSourceChange}
          disabled={type === "template"}
          sx={{ paddingBottom: 2 }}
        >
          <ToggleButton value="current">
            <FormattedMessage defaultMessage="Current" />
          </ToggleButton>
          <ToggleButton value="saved">
            <FormattedMessage defaultMessage="Saved" />
          </ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{ paddingBottom: 2, alignSelf: "center" }}>
          <IconButton onClick={() => setSettingsVisible((old) => !old)}>{settingsVisible ? <SettingsIcon /> : <SettingsOutlinedIcon />}</IconButton>
        </Box>
        {process.env.NODE_ENV === "development" && (
          <Box sx={{ paddingBottom: 2, alignSelf: "center" }}>
            <Button
              startIcon={<RefreshIcon />}
              disabled={!(type === "device" && source === "current")}
              onClick={() => getDeviceConfig(currentValue)}
              color="inherit"
            >
              {formatMessage({ defaultMessage: "Update" })}
            </Button>
          </Box>
        )}
      </Stack>
      {settingsVisible && (
        <Stack direction="row" spacing={4} alignSelf="center">
          <ToggleButtonGroup color="primary" value={display} onChange={(event, newDisplay) => setDisplay(newDisplay)} aria-label="text formatting">
            <ToggleButton value="size">
              <FormattedMessage defaultMessage="Object size" />
            </ToggleButton>
            <ToggleButton value="dataType">
              <FormattedMessage defaultMessage="Data types" />
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      )}
      {type !== "template" && source === "current" && errors.length > 0 && (
        <>
          <Typography variant="h5" color="error">
            <FormattedMessage defaultMessage="Errors" />
          </Typography>
          {errors.map((error, i) => (
            <Box key={`error-${i}`}>
              <Typography variant="h6" color="error">
                {error.title}
              </Typography>
              {error.details && (
                <ul style={{ margin: 0 }}>
                  {error.details.map((detail, j) => (
                    <Typography component="li" key={`error-detail-${i}-${j}`} color="error">
                      {detail}
                    </Typography>
                  ))}
                </ul>
              )}
            </Box>
          ))}
        </>
      )}
      {errMsg && <Typography color="error">{errMsg}</Typography>}
      {loadConfig && (
        <Button variant="outlined" color="warning" sx={{ alignSelf: "flex-start", mb: 1 }} onClick={() => loadDefaultConfig()} disabled={defaultConfigLoading}>
          {formatMessage({ defaultMessage: "Reset config to default" })}
        </Button>
      )}
      {/* {source === "saved" && type === "toolbox" && (
        <Alert severity="warning" variant="outlined">
          <Typography variant="body2">
            A "Mentett" Toolbox configban szerepelhet olyan érték, ami a valóságban nincs elmentve. Ez akkor fordulhat elő, ha készüléknek küldött config
            alapján lett generálva, vagy még akkor lett elmentve a legutóbbi config, amikor nem töltötte ki a default értékekkel. Ha kell, lehet betenni egy
            tényleges mentett lekérdezést.
          </Typography>
          <Typography variant="body2">A készüléknek küldött configból látszik, ha a valóban mentettből hiányoznak értékek.</Typography>
        </Alert>
      )} */}
      {sample && typeof sample === "object" ? (
        <ReactJson
          src={type === "toolbox" ? keySort(sample) : sample}
          name={null}
          quotesOnKeys={false}
          indentWidth={2}
          displayObjectSize={display.includes("size")}
          displayDataTypes={display.includes("dataType")}
        />
      ) : (
        <>
          {sample && typeof sample === "string" && (
            <Button startIcon={<ContentCopyIcon />} onClick={() => copyToClipboard(sample)} color="inherit" sx={{ alignSelf: "flex-start" }}>
              {formatMessage({ defaultMessage: "Copy to clipboard" })}
            </Button>
          )}
          <Typography sx={{ wordBreak: "break-word", fontFamily: "monospace" }}>{typeof sample === "string" ? sample : JSON.stringify(sample)}</Typography>
        </>
      )}
    </>
  );
};

export default ConfigSample;
